import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff, ShieldAlert, KeyRound, RefreshCw, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lockState, setLockState] = useState(null); // null | 'soft' | 'hard_admin' | 'forced_reset'
  const [lockMessage, setLockMessage] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPw, setConfirmNewPw] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const { login } = useAdmin();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLockState(null);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Invalid credentials';

      if (status === 423) {
        // Determine if forced reset or admin unlock
        if (detail.toLowerCase().includes('reset your password') || detail.toLowerCase().includes('otp')) {
          setLockState('forced_reset');
        } else {
          setLockState('hard_admin');
        }
        setLockMessage(detail);
      } else if (status === 429) {
        setLockState('soft');
        setLockMessage(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Super admin unlock (verification code only)
  const handleAdminUnlock = async () => {
    if (verifyCode.length !== 6) { toast.error('Enter 6-digit code'); return; }
    setUnlocking(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verifyCode }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Account unlocked! You can now log in.');
        resetState();
      } else {
        toast.error(data.detail || 'Invalid code');
        if (res.status === 410) toast.info('New code sent to your email.');
      }
    } catch { toast.error('Network error'); }
    finally { setUnlocking(false); }
  };

  // Regular user forced password reset (OTP + new password)
  const handleForcedReset = async () => {
    if (verifyCode.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmNewPw) { toast.error('Passwords do not match'); return; }
    setUnlocking(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/forced-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verifyCode, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password reset! You can now log in with your new password.');
        resetState();
      } else {
        toast.error(data.detail || 'Failed to reset');
        if (res.status === 410) toast.info('New OTP sent to your email.');
      }
    } catch { toast.error('Network error'); }
    finally { setUnlocking(false); }
  };

  const handleResendCode = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: '' }),
      });
      if (res.ok) toast.success('New code sent to email');
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const resetState = () => {
    setLockState(null);
    setVerifyCode('');
    setNewPassword('');
    setConfirmNewPw('');
    setLockMessage('');
  };

  // ─── FORCED PASSWORD RESET SCREEN (regular users) ───
  if (lockState === 'forced_reset') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-800/50 bg-slate-800/50 backdrop-blur" data-testid="forced-reset-card">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-4">
              <RotateCcw className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-amber-400">Password Reset Required</CardTitle>
            <CardDescription className="text-slate-400">
              Your account has been locked. An OTP has been sent to <strong className="text-white">{email}</strong>. Enter it below with a new password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">OTP Code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text" maxLength={6} placeholder="6-digit OTP"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white text-center text-xl tracking-[0.5em] font-mono placeholder:tracking-normal placeholder:text-base placeholder:text-slate-500"
                  data-testid="reset-otp-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">New Password</Label>
              <Input
                type="password" placeholder="Minimum 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="reset-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Confirm New Password</Label>
              <Input
                type="password" placeholder="Repeat password"
                value={confirmNewPw}
                onChange={(e) => setConfirmNewPw(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="reset-confirm-password"
              />
              {confirmNewPw && newPassword !== confirmNewPw && (
                <p className="text-xs text-red-400">Passwords do not match</p>
              )}
            </div>
            <Button
              onClick={handleForcedReset}
              disabled={unlocking || verifyCode.length !== 6 || newPassword.length < 8 || newPassword !== confirmNewPw}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              data-testid="reset-submit-btn"
            >
              {unlocking ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Resetting...</> : 'Reset Password & Unlock'}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button onClick={handleResendCode} className="text-slate-400 hover:text-white transition-colors" data-testid="resend-otp-btn">Resend OTP</button>
              <button onClick={resetState} className="text-slate-400 hover:text-white transition-colors" data-testid="back-to-login-forced">Back to Login</button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── SUPER ADMIN LOCKOUT SCREEN (verification code only) ───
  if (lockState === 'hard_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-800/50 bg-slate-800/50 backdrop-blur" data-testid="lockout-card">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <ShieldAlert className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-red-400">Account Locked</CardTitle>
            <CardDescription className="text-slate-400">
              A 6-digit verification code has been sent to the registered admin email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="bg-red-950/30 border border-red-800/30 rounded-lg p-3 text-sm text-red-300">{lockMessage}</div>
            <div className="space-y-2">
              <Label className="text-slate-300">Verification Code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text" maxLength={6} placeholder="Enter 6-digit code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white text-center text-xl tracking-[0.5em] font-mono placeholder:tracking-normal placeholder:text-base placeholder:text-slate-500"
                  data-testid="verify-code-input"
                />
              </div>
            </div>
            <Button
              onClick={handleAdminUnlock}
              disabled={unlocking || verifyCode.length !== 6}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
              data-testid="unlock-btn"
            >
              {unlocking ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : 'Unlock Account'}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button onClick={handleResendCode} className="text-slate-400 hover:text-white transition-colors" data-testid="resend-code-btn">Resend Code</button>
              <button onClick={resetState} className="text-slate-400 hover:text-white transition-colors" data-testid="back-to-login-btn">Back to Login</button>
            </div>
            <p className="text-center text-xs text-slate-500">All login attempts are logged and monitored.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── NORMAL LOGIN ───
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Admin Portal</CardTitle>
          <CardDescription className="text-slate-400">Sign in to access the Dnotours management dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          {lockState === 'soft' && (
            <div className="mb-5 bg-amber-950/30 border border-amber-700/30 rounded-lg p-3 text-sm text-amber-300" data-testid="soft-lock-warning">
              {lockMessage}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email" type="email" placeholder="you@dnotours.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="pl-10 pr-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500"
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading || lockState === 'soft'} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5" data-testid="login-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <div className="mt-6 pt-6 border-t border-slate-700">
            <p className="text-center text-sm text-slate-500">Authorized personnel only. All activities are logged.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
